import React, { useEffect } from "react";
import { SectionHeader } from "../Header/SectionHeader";
import InputField from "../input/inputField";
import { useDispatch, useSelector } from "react-redux";
import {
  setAddress,
  setClassId,
  setDOB,
  setFirstName,
  setGender,
  setLastName,
  setMiddleName,
  setNationality,
  setRegisteredAt,
  setReligion,
  setSchoolBranchId,
  setTransportId,
} from "../../Redux/Slices/studentAdmission.slice";
import { RootState } from "../../Redux/store";
import { DateField } from "../input/DateField";
import Dropdown from "../Dropdown/dropdown";
import { useGetSchoolBranchesOptions } from "../../hooks/branchHooks/branch.hooks";
import { DropdownOptions } from "../dropdownOptions";
import { useGetClassesOptions } from "../../hooks/classHooks/class.hooks";
import { GENDER_OPTIONS } from "../../Constants/options/gender.options";
import { getSelectedOptionFromValue } from "../../Helper/options.helper";
import { useGetTransportOptions } from "../../hooks/transportHook/transport.hooks";
import { NepaliDateField } from "../input/NepaliDateField";
import { convertADToBS } from "../../utils/date.utils";
import { AdBsCalendar } from "../Calendar/AdBsCalendar";

const StudentAdmissionFormStepTwo = () => {
  const {
    firstName,
    middleName,
    lastName,
    dateOfBirth,
    address,
    parentsAddress,
    nationality,
    religion,
    schoolBranchId,
    classId,
    gender,
    transportId,
    registeredAt,
  } = useSelector((state: RootState) => ({
    firstName: state.studentAdmission.firstName,
    middleName: state.studentAdmission.middleName,
    lastName: state.studentAdmission.lastName,
    dateOfBirth: state.studentAdmission.dateOfBirth,
    address: state.studentAdmission.address,
    parentsAddress: state.studentAdmission.selectedParents?.address || "",
    nationality: state.studentAdmission.nationality,
    religion: state.studentAdmission.religion,
    schoolBranchId: state.studentAdmission.schoolBranchId,
    classId: state.studentAdmission.classId,
    gender: state.studentAdmission.gender,
    transportId: state.studentAdmission.transportId,
    registeredAt: state.studentAdmission.registeredAt,
  }));
  const dispatch = useDispatch();

  useEffect(() => {
    // This is to set the address of the student as a address of parents as initial.
    if (parentsAddress) {
      dispatch(setAddress(parentsAddress));
    }
  }, [parentsAddress]);

  const { data: BRANCHES_OPTIONS } = useGetSchoolBranchesOptions();
  const { data: CLASSES_OPTIONS } = useGetClassesOptions();

  const { data: TRANSPORT_OPTIONS } = useGetTransportOptions();

  const BSDate = convertADToBS(dateOfBirth);

  const setBSDate = ({
    bsDate,
    adDate,
  }: {
    bsDate: string;
    adDate: string;
  }) => {
    dispatch(setDOB(adDate));
  };

  return (
    <div>
      <div className="w-full">
        <SectionHeader
          headerText="Streamlined Class & Branch Management"
          subText="Your chosen class and school branch will be assigned to the student during the enrollment process."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8">
          <Dropdown
            label="Class"
            options={CLASSES_OPTIONS || []}
            selectedOption={
              (CLASSES_OPTIONS || []).find(
                ({ value }) => value === classId
              ) as DropdownOptions
            }
            setSelectedOption={(selectedOption: DropdownOptions) => {
              dispatch(setClassId(selectedOption.value as number));
            }}
          />
          <Dropdown
            label="School Branch"
            options={BRANCHES_OPTIONS || []}
            selectedOption={
              (BRANCHES_OPTIONS || []).find(
                ({ value }) => value === schoolBranchId
              ) as DropdownOptions
            }
            setSelectedOption={(selectedOption: DropdownOptions) => {
              dispatch(setSchoolBranchId(selectedOption.value as number));
            }}
          />
        </div>

        <SectionHeader
          headerText="Transportation Details"
          subText="The transportation section collects information to ensure safe and efficient commuting for students. All details are kept confidential and used solely for this purpose."
        />

        <div className="grid grid-cols-4 gap-x-8">
          <Dropdown
            label="Transportation"
            options={TRANSPORT_OPTIONS || []}
            selectedOption={
              (TRANSPORT_OPTIONS || []).find(
                ({ value }: { value: number | null }) => value === transportId
              ) as DropdownOptions
            }
            setSelectedOption={(selectedOption: DropdownOptions) => {
              dispatch(setTransportId(selectedOption.value as number));
            }}
            canBeEmpty
          />
        </div>

        <SectionHeader
          headerText="Student information"
          subText="Information in the Student Form is exclusively utilized for enrollment purposes, ensuring a swift and confidential admission process."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8">
          <InputField
            value={firstName}
            setValue={(value) => {
              dispatch(setFirstName(value));
            }}
            label="First name *"
            name="new-student-firstname"
            isRequiredFocusonValidation
          />
          <InputField
            value={middleName}
            setValue={(value) => {
              dispatch(setMiddleName(value));
            }}
            label="Middle name *"
            name="new-student-middlename"
          />
          <InputField
            value={lastName}
            setValue={(value) => {
              dispatch(setLastName(value));
            }}
            label="Last name *"
            name="new-student-lastname"
            isRequiredFocusonValidation
          />
          <Dropdown
            label="Gender"
            options={GENDER_OPTIONS || []}
            selectedOption={getSelectedOptionFromValue(GENDER_OPTIONS, gender)}
            setSelectedOption={(selectedOption: DropdownOptions) => {
              dispatch(setGender(selectedOption.value as string));
            }}
          />
          <DateField
            setDate={(value) => {
              dispatch(setDOB(value));
            }}
            date={dateOfBirth}
            label="Date of birth (A.D)"
          />

          <NepaliDateField
            label={"Date of birth (B.S)"}
            date={BSDate}
            setDate={setBSDate}
          />

          <InputField
            value={nationality}
            setValue={(value) => {
              dispatch(setNationality(value));
            }}
            label="Nationality*"
            name="new-student-nationality"
            isRequiredFocusonValidation
          />
          <InputField
            value={religion}
            setValue={(value) => {
              dispatch(setReligion(value));
            }}
            label="Religion*"
            name="new-student-religion"
            isRequiredFocusonValidation
          />
          <InputField
            value={address}
            setValue={(value) => {
              dispatch(setAddress(value));
            }}
            label="Address*"
            name="new-student-address"
            isRequiredFocusonValidation
          />

          <AdBsCalendar
            date={registeredAt}
            setDate={(value) => dispatch(setRegisteredAt(value))}
            title={"Registered at"}
          />
        </div>
      </div>
    </div>
  );
};

export default StudentAdmissionFormStepTwo;
